// Jquery
import $ from 'jquery';

// App components
import LeftSidebar from './left-sidebar.js';
import Topbar from './topbar.js';

const SIDEBAR_THEME_DEFAULT = 'default';
const SIDEBAR_THEME_LIGHT = 'light';
const SIDEBAR_THEME_DARK = 'dark';

const DEFAULT_CONFIG = {
	sideBarTheme: SIDEBAR_THEME_DEFAULT,
	isBoxed: false,
	isCondensed: false,
	isScrollable: false,
	isDarkModeEnabled: false,
};

/**
 * Layout and theme manager
 */
export default class LayoutThemeApp {

	constructor() {
		this.body = $('body');
		this.window = $(window);
		this._config = {};
		this.defaultSelectedStyle = null;
		this.leftSidebar = new LeftSidebar();
		this.topBar = new Topbar();
	}

	/**
	 * Preserves the config
	 */
	_saveConfig(newConfig) {
		$.extend(this._config, newConfig);
	}

	/**
	 * Get the stored config
	 */
	_getStoredConfig() {
		const config = DEFAULT_CONFIG;
		const layoutTheme = this.body.attr('data-layout-color');

		if (layoutTheme && layoutTheme === 'dark') {
			config['isDarkModeEnabled'] = true;
		}

		config['sideBarTheme'] = this.body.attr('data-leftbar-theme');

		const layoutMode = this.body.attr('data-layout-mode');
		if (layoutMode === 'boxed') {
			config['isBoxed'] = true;
		}
		const leftbarMode = this.body.attr('data-leftbar-compact-mode');
		if (leftbarMode === 'condensed') {
			config['isCondensed'] = true;
		}
		else if (leftbarMode === 'scrollable') {
			config['isScrollable'] = true;
		}
		return config;
	}

	/**
	 * Apply the given config and sets the layout and theme
	 */
	_applyConfig() {
		// getting the saved config if available
		this._config = this._getStoredConfig();

		// activate menus
		this.leftSidebar.init();

		// sets the theme
		switch (this._config.sideBarTheme) {
			case SIDEBAR_THEME_DARK: {
				this.activateDarkSidebarTheme();
				break;
			}
			case SIDEBAR_THEME_LIGHT: {
				this.activateLightSidebarTheme();
				break;
			}
			case SIDEBAR_THEME_DEFAULT: {
				this.activateDefaultSidebarTheme();
				break;
			}
		}

		// enable or disable the dark mode
		if (this._config.isDarkModeEnabled) {
			this.activateDarkMode();
		}
		else {
			this.deactivateDarkMode();
		}

		// sets the boxed
		if (this._config.isBoxed) {
			this.activateBoxed();
		}

		// sets condensed view
		if (this._config.isCondensed) {
			this.activateCondensedSidebar();
		}

		// sets scrollable navbar
		if (this._config.isScrollable) {
			this.activateScrollableSidebar();
		}
	}

	/**
	 * Initializes the layout
	 */
	_adjustLayout() {
		// in case of small size, add class enlarge to have minimal menu
		if (this.window.width() <= 1028) {
			this.activateCondensedSidebar(true);
		}
		else {
			const config = this._getStoredConfig();
			if (!config.isCondensed && !config.isScrollable) {
				this.deactivateCondensedSidebar();
			}
		}
	}
	/**
	 * Activate fluid mode
	 */
	activateFluid() {
		this._saveConfig({ isBoxed: false });
		this.body.attr('data-layout-mode', 'fluid');
	}
	/**
	 * Activate boxed mode
	 */
	activateBoxed() {
		this._saveConfig({ isBoxed: true });
		this.body.attr('data-layout-mode', 'boxed');
	}
	/**
	 * Activates the condensed side bar
	 */
	activateCondensedSidebar(ignoreToStore) {
		if (!ignoreToStore) {
			this._saveConfig({
				isCondensed: true, isScrollable: false,
			});
		}
		this.leftSidebar.activateCondensedSidebar();
	}
	/**
	 * Deactivates the condensed side bar
	 */
	deactivateCondensedSidebar() {
		this._saveConfig({ isCondensed: false });
		this.leftSidebar.deactivateCondensedSidebar();
	}
	/**
	 * Activates the scrollable sidenar
	 */
	activateScrollableSidebar() {
		this._saveConfig({ isScrollable: true, isCondensed: false });
		this.leftSidebar.activateScrollableSidebar();
	}
	/**
	 * Deactivates the scrollable sidenar
	 */
	deactivateScrollableSidebar() {
		this._saveConfig({ isScrollable: false });
		this.leftSidebar.deactivateScrollableSidebar();
	}
	/**
	 * Activates the default theme
	 */
	activateDefaultSidebarTheme() {
		this.leftSidebar.activateDefaultTheme();
		this._saveConfig({ sideBarTheme: SIDEBAR_THEME_DEFAULT });
	}
	/**
	 * Activates the light theme
	 */
	activateLightSidebarTheme() {
		// this._resetLayout();
		this.leftSidebar.activateLightTheme();
		this._saveConfig({ sideBarTheme: SIDEBAR_THEME_LIGHT });

	}
	/**
	 * Activates the dark theme
	 */
	activateDarkSidebarTheme() {
		// this._resetLayout();
		this.leftSidebar.activateDarkTheme();
		this._saveConfig({ sideBarTheme: SIDEBAR_THEME_DARK });

	}
	/**
	 * toggle the dark mode
	 */
	activateDarkMode() {
		document.body.dataset.layoutColor = 'dark';

		if (this.body.attr('data-layout') === 'detached') {
			this.leftSidebar.activateDarkTheme();
			this._saveConfig({ isDarkModeEnabled: true, sideBarTheme: SIDEBAR_THEME_DARK });
		}
		else {
			this._saveConfig({ isDarkModeEnabled: true });
		}
	}

	/**
	 * Deactivate the dark mode
	 */
	deactivateDarkMode() {
		document.body.dataset.layoutColor = 'light';

		this._saveConfig({ isDarkModeEnabled: false });
	}

	/**
	 * Clear out the saved config
	 */
	clearSavedConfig() {
		this._config = DEFAULT_CONFIG;
	}

	/**
	 * Gets the config
	 */
	getConfig() {
		return this._config;
	}

	/**
	 * Reset to default
	 */
	reset(callback) {
		this.clearSavedConfig();

		if ($('#main-style-container').length > 0) {
			this.defaultSelectedStyle = $('#main-style-container').attr('href');
		}

		this.deactivateCondensedSidebar();
		this.deactivateDarkMode();
		this.activateDefaultSidebarTheme();
		this.activateFluid();
		// calling the call back to let the caller know that it's done
		callback();
	}

	init() {
		if ($('#main-style-container').length > 0) {
			this.defaultSelectedStyle = $('#main-style-container').attr('href');
		}

		// initialize the menu
		this._applyConfig();
		// adjust layout based on width
		this._adjustLayout();

		// on window resize, make menu flipped automatically
		this.window.on('resize', (e) => {
			e.preventDefault();
			this._adjustLayout();
		});

		// topbar
		this.topBar.init();
	}
}
